import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  VStack,
  Text,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import {
  formatMusicoInstrumentosSinVoz,
  isMusicoDirectorPlantilla,
  sortMusicosPlantillaMicrosite,
  musicosPreparadosPlantillaMicrositio,
} from '../../utils/musicoInstrumentos';
import { readPlantillaMusicosInstrumentosModo, readPlantillaMusicosInstrumentoPorMusico } from './micrositeUtils.js';
import { ScrollReveal } from './MicrositeScrollReveal.jsx';

/**
 * Bloque «plantilla de músicos» en Sobre nosotros (foto, nombre, instrumentos sin voz).
 * Orden: director(es) centrados; luego familias (madera → metal → percusión → resto).
 * `variant` ajusta contenedor según tema del micrositio.
 */
export default function MicrositePlantillaMusicos({ musicos, micrositeConfig, primaryColor = '#7c3aed', variant = 'moderno' }) {
  const list = useMemo(() => {
    const raw = Array.isArray(musicos) ? musicos.filter(Boolean) : [];
    const modo = readPlantillaMusicosInstrumentosModo(micrositeConfig);
    const porMusico = readPlantillaMusicosInstrumentoPorMusico(micrositeConfig);
    const prepared = musicosPreparadosPlantillaMicrositio(raw, modo, porMusico);
    return sortMusicosPlantillaMicrosite(prepared);
  }, [musicos, micrositeConfig]);
  if (!list.length) return null;

  const directores = list.filter(isMusicoDirectorPlantilla);
  const gridList = list.filter((m) => !isMusicoDirectorPlantilla(m));
  const nGrid = gridList.length;
  const mdCols = nGrid >= 9 ? 4 : 3;

  const nombreCompleto = (m) => `${m.nombre || ''} ${m.apellidos || ''}`.trim() || '—';

  const cardProps =
    variant === 'clasico'
      ? { bg: 'white', borderWidth: '1px', borderColor: 'gray.200', rounded: 'md' }
      : variant === 'elegante'
        ? { bg: 'white', borderWidth: '1px', borderColor: 'gray.100', rounded: 'none' }
        : variant === 'aurora'
          ? { bg: 'gray.800', borderWidth: '1px', borderColor: 'whiteAlpha.100', rounded: 'xl' }
          : variant === 'vortex'
            ? { bg: 'gray.800', borderWidth: '1px', borderColor: 'whiteAlpha.200', rounded: 'xl', shadow: '0 10px 26px -14px rgba(34,211,238,0.35)' }
            : variant === 'glass'
              ? { bg: 'whiteAlpha.700', borderWidth: '1px', borderColor: 'whiteAlpha.800', rounded: 'xl', backdropFilter: 'blur(8px)' }
              : variant === 'kinetic'
                ? { bg: 'white', borderWidth: '1px', borderColor: 'gray.200', rounded: 'lg', shadow: 'sm' }
          : variant === 'editorial'
            ? { bg: 'white', borderWidth: '1px', borderColor: 'gray.300', rounded: 'none', shadow: 'sm' }
            : variant === 'pulso'
              ? { bg: 'white', borderWidth: '3px', borderColor: 'black', rounded: 'none', boxShadow: '4px 4px 0 #000' }
              : { bg: 'white', borderWidth: '1px', borderColor: 'gray.100', rounded: 'xl', shadow: 'sm' };

  const stagger = (i) => Math.min(0.55, i * 0.06);

  const titleColor = (variant === 'aurora' || variant === 'vortex') ? 'gray.100' : 'gray.800';
  const subColor = (variant === 'aurora' || variant === 'vortex') ? 'gray.400' : 'gray.500';
  const avatarBg = (variant === 'aurora' || variant === 'vortex') ? 'whiteAlpha.200' : 'purple.100';
  const avatarColor = (variant === 'aurora' || variant === 'vortex') ? 'whiteAlpha.900' : 'purple.700';

  const renderCard = (m, revealIndex) => (
    <ScrollReveal key={m.id} delay={stagger(revealIndex)} w="full">
      <VStack gap={2} align="center" py={4} px={3} {...cardProps}>
        <Avatar.Root size="md" bg={avatarBg} color={avatarColor}><Avatar.Fallback name={nombreCompleto(m)} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
        <Text fontWeight="600" fontSize="sm" textAlign="center" lineClamp={2} color={titleColor}>
          {nombreCompleto(m)}
        </Text>
        <Text fontSize="xs" color={subColor} textAlign="center" lineClamp={3} lineHeight="1.4">
          {formatMusicoInstrumentosSinVoz(m)}
        </Text>
        {(variant === 'moderno' || variant === 'aurora' || variant === 'vortex' || variant === 'glass' || variant === 'kinetic' || variant === 'pulso') && (
          <Box
            w="32px"
            h="2px"
            bg={primaryColor}
            rounded="full"
            opacity={variant === 'aurora' ? 0.55 : 0.35}
            mt={1}
          />
        )}
      </VStack>
    </ScrollReveal>
  );

  return (
    <Box mt={10} w="full">
      <Text
        fontSize="sm"
        fontWeight="700"
        textAlign="center"
        textTransform={variant === 'elegante' || variant === 'editorial' ? 'uppercase' : 'none'}
        letterSpacing={variant === 'elegante' || variant === 'editorial' ? '0.12em' : 'normal'}
        color={variant === 'aurora' ? 'gray.300' : variant === 'pulso' ? 'gray.900' : 'gray.700'}
        mb={4}
      >
        Plantilla de músicos
      </Text>

      {directores.length > 0 && (
        <Flex justify="center" align="stretch" wrap="wrap" gap={4} mb={gridList.length ? 6 : 0}>
          {directores.map((m, i) => (
            <Box key={m.id} flex="0 1 260px" maxW="280px" w="100%">
              {renderCard(m, i)}
            </Box>
          ))}
        </Flex>
      )}

      {gridList.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: nGrid > 2 ? 2 : 1, md: mdCols }} gap={4}>
          {gridList.map((m, i) => renderCard(m, directores.length + i))}
        </SimpleGrid>
      )}
    </Box>
  );
}
